import { apiClient } from './client';
import type { PagedResult, SystemAccount, SystemAccountWrite } from '@/types/oscar';

export interface AccountsQuery {
  subtype?: string;
  source?: string;
  search?: string;
  needsTriage?: boolean;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function toQueryString(q: AccountsQuery): string {
  const params = new URLSearchParams();
  if (q.subtype) params.set('subtype', q.subtype);
  if (q.source) params.set('source', q.source);
  if (q.search) params.set('search', q.search);
  if (q.needsTriage) params.set('needsTriage', '1');
  if (q.sort) params.set('sort', q.sort);
  if (q.dir) params.set('dir', q.dir);
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** GET /api/accounts — filtered, paginated list. */
export function listAccounts(query: AccountsQuery = {}, signal?: AbortSignal): Promise<PagedResult<SystemAccount>> {
  return apiClient.get<PagedResult<SystemAccount>>(`/api/accounts${toQueryString(query)}`, signal);
}

/** GET /api/accounts/:id. */
export function getAccount(id: number, signal?: AbortSignal): Promise<SystemAccount> {
  return apiClient.get<SystemAccount>(`/api/accounts/${id}`, signal);
}

/** POST /api/accounts — create (helpdesk + admin). */
export function createAccount(body: SystemAccountWrite): Promise<SystemAccount> {
  return apiClient.post<SystemAccount>('/api/accounts', body);
}

/** PATCH /api/accounts/:id. */
export function updateAccount(id: number, body: SystemAccountWrite): Promise<SystemAccount> {
  return apiClient.patch<SystemAccount>(`/api/accounts/${id}`, body);
}

/** POST /api/accounts/:id/delete — soft delete (admin only). */
export function deleteAccount(id: number): Promise<{ SystemAccountId: number; IsDeleted: boolean }> {
  return apiClient.post<{ SystemAccountId: number; IsDeleted: boolean }>(`/api/accounts/${id}/delete`);
}

/** POST /api/sync/system-accounts — admin-triggered Graph sync. */
export function syncSystemAccounts(): Promise<unknown> {
  return apiClient.post('/api/sync/system-accounts');
}
